/**
 * OrderResultsBar (v2)
 * 
 * Sort Results panel with:
 * 1. Quick Sort - one-click sorting presets (always visible)
 * 2. Keyword Boost - categorized keywords for fine-tuning (collapsible)
 * 
 * Design: Clear hierarchy, descriptive helper text, progressive disclosure
 */

import React, { useState, useRef, useEffect } from 'react';
import { ArrowUpDown, Plus, X, Check, ChevronDown, ChevronUp, Briefcase, Building2, Search } from 'lucide-react';

// Quick Sort presets
const SORT_PRESETS = [
  { id: 'balanced', label: 'Default', description: 'Balanced ranking' },
  { id: 'qualityFirst', label: 'Accuracy', description: 'Prioritize contact accuracy' },
  { id: 'locationFirst', label: 'Location', description: 'Prioritize location match' },
  { id: 'seniorityFirst', label: 'Seniority', description: 'Prioritize senior roles' },
];

// Preset weight configurations
const PRESET_WEIGHTS = {
  balanced: { titleMatch: 70, location: 60, seniority: 50, industry: 50, contactQuality: 40 },
  seniorityFirst: { titleMatch: 60, location: 40, seniority: 100, industry: 40, contactQuality: 30 },
  locationFirst: { titleMatch: 50, location: 100, seniority: 40, industry: 30, contactQuality: 20 },
  qualityFirst: { titleMatch: 50, location: 40, seniority: 40, industry: 30, contactQuality: 100 },
};

export default function OrderResultsBar({
  // Ranking
  rankingPreset = 'balanced',
  onRankingChange,
  
  // Categorized Keywords
  roleKeywords = [],
  companyKeywords = [],
  onAddRoleKeyword,
  onAddCompanyKeyword,
  onRemoveRoleKeyword,
  onRemoveCompanyKeyword,
  onClearAllKeywords,
  
  // Legacy support (if using old single-list keywords)
  keywords = [],
  onAddKeyword,
  onRemoveKeyword,
  onClearKeywords,
  
  // Match counts
  keywordMatchCount = 0,
  roleMatchCount = 0,
  companyMatchCount = 0,
  
  // Display
  totalResults = 0,
  loadedResults = 0,
  className = '',
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showKeywordBoost, setShowKeywordBoost] = useState(false);
  const [roleInput, setRoleInput] = useState('');
  const [companyInput, setCompanyInput] = useState('');
  const roleInputRef = useRef(null);
  const companyInputRef = useRef(null);
  const containerRef = useRef(null);
  
  // Determine if using new categorized keywords or legacy
  const useCategorized = onAddRoleKeyword && onAddCompanyKeyword;
  const hasRoleKeywords = roleKeywords.length > 0;
  const hasCompanyKeywords = companyKeywords.length > 0;
  const hasAnyKeywords = useCategorized ? (hasRoleKeywords || hasCompanyKeywords) : keywords.length > 0;
  const totalKeywordCount = useCategorized ? (roleKeywords.length + companyKeywords.length) : keywords.length;
  const totalMatchCount = useCategorized ? (roleMatchCount + companyMatchCount) : keywordMatchCount;
  
  // Close expanded view when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsExpanded(false);
      }
    };
    
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded]);
  
  const handlePresetChange = (presetId) => {
    if (onRankingChange && PRESET_WEIGHTS[presetId]) {
      onRankingChange(presetId, PRESET_WEIGHTS[presetId]);
    }
  };
  
  const handleAddRoleKeyword = () => {
    const kw = roleInput.trim().toLowerCase();
    if (kw) {
      if (useCategorized && onAddRoleKeyword) {
        onAddRoleKeyword(kw);
      } else if (onAddKeyword) {
        onAddKeyword(kw);
      }
      setRoleInput('');
    }
  };
  
  const handleAddCompanyKeyword = () => {
    const kw = companyInput.trim().toLowerCase();
    if (kw) {
      if (useCategorized && onAddCompanyKeyword) {
        onAddCompanyKeyword(kw);
      } else if (onAddKeyword) {
        onAddKeyword(kw);
      }
      setCompanyInput('');
    }
  };
  
  const handleClearAll = () => {
    if (useCategorized && onClearAllKeywords) {
      onClearAllKeywords();
    } else if (onClearKeywords) {
      onClearKeywords();
    }
  };
  
  const activePreset = SORT_PRESETS.find(p => p.id === rankingPreset) || SORT_PRESETS[0];
  
  // Collapsed view - single line with summary
  if (!isExpanded) {
    return (
      <div className={`${className}`}>
        <button
          onClick={() => setIsExpanded(true)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-white/80 backdrop-blur-sm border border-black/5 rounded-xl hover:border-black/10 transition-all group"
        >
          <div className="flex items-center gap-3">
            <ArrowUpDown className="w-4 h-4 text-black/30 group-hover:text-black/50 transition-colors" />
            <span className="text-sm text-black/70 font-medium">Sort Results</span>
            
            {/* Active state indicators */}
            <div className="flex items-center gap-2">
              {rankingPreset !== 'balanced' && (
                <span className="px-2 py-0.5 bg-[#5a20ff]/10 text-[#5a20ff] text-xs font-medium rounded-full">
                  {activePreset.label}
                </span>
              )}
              {hasAnyKeywords && (
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full flex items-center gap-1">
                  {totalKeywordCount} keyword{totalKeywordCount > 1 ? 's' : ''}
                  {totalMatchCount > 0 && (
                    <span className="text-emerald-600">· {totalMatchCount} matches</span>
                  )}
                </span>
              )}
            </div>
          </div>
          
          <span className="text-xs text-black/30 group-hover:text-black/50 transition-colors">
            Click to customize
          </span>
        </button>
      </div>
    );
  }
  
  // Expanded view - full controls
  return (
    <div ref={containerRef} className={`${className}`}>
      <div className="bg-white border border-black/10 rounded-xl shadow-lg shadow-black/5 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-[#5a20ff]" />
            <span className="text-sm font-semibold text-black">Sort Results</span>
          </div>
          <button
            onClick={() => setIsExpanded(false)}
            className="p-1 hover:bg-black/5 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-black/40" />
          </button>
        </div>
        
        {/* Quick Sort Presets - Always visible */}
        <div className="px-4 py-3 border-b border-black/5">
          <p className="text-[10px] text-black/40 mb-2 uppercase tracking-wider font-semibold">Quick Sort</p>
          <div className="flex flex-wrap gap-2">
            {SORT_PRESETS.map(preset => (
              <button
                key={preset.id}
                onClick={() => handlePresetChange(preset.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  rankingPreset === preset.id
                    ? 'bg-[#5a20ff] text-white shadow-sm'
                    : 'bg-black/5 text-black/60 hover:bg-black/10 hover:text-black/80'
                }`}
                title={preset.description}
              >
                {rankingPreset === preset.id && <Check className="w-3 h-3 inline mr-1" />}
                {preset.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Keyword Boost Section - Collapsible */}
        <div className="border-b border-black/5">
          <button
            onClick={() => setShowKeywordBoost(!showKeywordBoost)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-black/[0.02] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-black/40" />
              <span className="text-sm font-medium text-black/70">Keyword Boost</span>
              {hasAnyKeywords && (
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-semibold rounded-full">
                  {totalKeywordCount} active
                </span>
              )}
            </div>
            {showKeywordBoost ? (
              <ChevronUp className="w-4 h-4 text-black/30" />
            ) : (
              <ChevronDown className="w-4 h-4 text-black/30" />
            )}
          </button>
          
          {showKeywordBoost && (
            <div className="px-4 pb-4 space-y-4">
              {/* Role Keywords */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Briefcase className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs font-semibold text-black/70">Role Keywords</span>
                </div>
                <p className="text-[10px] text-black/40 mb-2">
                  Searches job titles, skills, descriptions, certifications
                </p>
                
                {/* Active role keywords */}
                {hasRoleKeywords && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {roleKeywords.map(kw => (
                      <span
                        key={kw}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-md"
                      >
                        {kw}
                        <button
                          onClick={() => onRemoveRoleKeyword?.(kw)}
                          className="hover:text-red-500 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Role keyword input */}
                <div className="flex items-center gap-2">
                  <input
                    ref={roleInputRef}
                    type="text"
                    value={roleInput}
                    onChange={(e) => setRoleInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddRoleKeyword()}
                    placeholder="e.g. clinical trials, CRA, director..."
                    className="flex-1 px-3 py-1.5 text-xs text-black bg-black/5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 placeholder-black/30"
                  />
                  <button
                    onClick={handleAddRoleKeyword}
                    disabled={!roleInput.trim()}
                    className="px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              
              {/* Company Keywords */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-3.5 h-3.5 text-purple-500" />
                  <span className="text-xs font-semibold text-black/70">Company Keywords</span>
                </div>
                <p className="text-[10px] text-black/40 mb-2">
                  Searches company name, industry, description
                </p>
                
                {/* Active company keywords */}
                {hasCompanyKeywords && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {companyKeywords.map(kw => (
                      <span
                        key={kw}
                        className="flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-md"
                      >
                        {kw}
                        <button
                          onClick={() => onRemoveCompanyKeyword?.(kw)}
                          className="hover:text-red-500 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Company keyword input */}
                <div className="flex items-center gap-2">
                  <input
                    ref={companyInputRef}
                    type="text"
                    value={companyInput}
                    onChange={(e) => setCompanyInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCompanyKeyword()}
                    placeholder="e.g. pharma, biotech, startup..."
                    className="flex-1 px-3 py-1.5 text-xs text-black bg-black/5 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/30 placeholder-black/30"
                  />
                  <button
                    onClick={handleAddCompanyKeyword}
                    disabled={!companyInput.trim()}
                    className="px-3 py-1.5 bg-purple-500 text-white text-xs font-medium rounded-lg hover:bg-purple-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              
              {/* Clear all + Match count */}
              {hasAnyKeywords && (
                <div className="flex items-center justify-between pt-2 border-t border-black/5">
                  <button
                    onClick={handleClearAll}
                    className="text-xs text-black/40 hover:text-black/60 transition-colors"
                  >
                    Clear all keywords
                  </button>
                  {totalMatchCount > 0 && (
                    <p className="text-xs text-emerald-600 font-medium">
                      {totalMatchCount} of {loadedResults || totalResults} loaded results boosted
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer hint */}
        <div className="px-4 py-2 bg-black/[0.02]">
          <p className="text-[10px] text-black/40 text-center">
            Sorting and keywords work together to surface the best results
          </p>
        </div>
      </div>
    </div>
  );
}
